<template>
    <v-container class="pa-5" fluid>
        <ConfirmDialog
            :is-open="deleteProductionSiteDialog"
            :data="currentProductionSite"
            removal
            @on-confirm="deleteProductionSite"
            @on-abort="closeDeleteProductionSiteDialog"
        >
            <template #title>
                Usuwanie stanowiska raportowania
            </template>
            <template #content>
                Czy na pewno chcesz usunąć stanowisko raportowania?
            </template>
            <template #confirmButton>
                Usuń
            </template>
        </ConfirmDialog>
        <ProductionSiteDialog
            :is-open="productionSiteDialog"
            :production-site-data="currentProductionSite"
            :types="types"
            :available-printers="printers"
            :machines="machines"
            :allowed-report-item-types="allowedReportItemTypes"
            @on-save="saveProductionSite"
            @on-close="closeProductionSiteDialog"
        />
        <page-title>Lista stanowisk raportowania</page-title>
        <v-row align="center">
            <v-col cols="12" sm="6" md="4">
                <v-btn
                    color="secondary"
                    medium
                    data-sentry-label="Dodaj stanowisko raportowania"
                    @click="showCreateProductionSiteDialog()"
                >
                    <v-icon left>
                        mdi-plus
                    </v-icon>
                    Dodaj stanowisko raportowania
                </v-btn>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-container fluid class="pa-0">
                    <v-row justify="end" dense>
                        <v-col cols="12" sm="5" md="4" lg="3" xl="2">
                            <v-select
                                v-model="filters.type_id"
                                :items="types"
                                item-value="id"
                                item-text="name"
                                placeholder="Typy..."
                                no-data-text="Brak danych..."
                                hide-details
                                solo
                                clearable
                                @keyup.enter="changeFilter"
                                @input="changeFilter"
                                @click:clear="clearFilter('type_id')"
                            />
                        </v-col>
                        <v-col cols="12" sm="5" md="4" lg="3" xl="2">
                            <v-text-field
                                v-model="filters.search"
                                append-icon="mdi-magnify"
                                label="Szukaj..."
                                single-line
                                clearable
                                hide-details
                                solo
                                @keyup.enter="changeFilter"
                                @click:append="changeFilter"
                                @click:clear="clearFilter('search')"
                            />
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="productionSites"
                        :loading="productionSitesLoading"
                        :footer-props="footerProps"
                        :page.sync="pagination.page"
                        :server-items-length="pagination.itemsLength"
                        :mobile-breakpoint="0"
                        name="ProductionSiteData"
                        @pagination="updatePagination"
                    >
                        <template #item.name="{item}">
                            {{ item.name }}
                        </template>
                        <template #item.machine="{item}">
                            {{ item.machine.name }}
                        </template>
                        <template #item.type="{item}">
                            {{ item.type.name }}
                        </template>
                        <template #item.allowsSendingOrders="{item}">
                            <v-icon
                                v-if="item.allowsSendingOrders"
                                small
                                color="success"
                            >
                                mdi-check
                            </v-icon>
                            <v-icon v-else small color="error">
                                mdi-close
                            </v-icon>
                        </template>
                        <template #item.actions="{item}">
                            <div class="d-inline-flex">
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Otwórz"
                                            v-on="on"
                                            @click="showEditProductionSiteDialog(item)"
                                        >
                                            <v-icon color="blue">
                                                mdi-ballot
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Otwórz</span>
                                </v-tooltip>
                                <v-tooltip :open-delay="tooltipDelay" top>
                                    <template #activator="{on}">
                                        <v-btn
                                            icon
                                            data-sentry-label="Usuń"
                                            v-on="on"
                                            @click="showDeleteProductionSiteDialog(item)"
                                        >
                                            <v-icon color="pink">
                                                mdi-delete
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>Usuń</span>
                                </v-tooltip>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/store'

import ProductionSiteDialog from './ProductionSiteDialog'
import PageTitle from '@/components/common/PageTitle'
import ConfirmDialog from '@/components/common/ConfirmDialog'

import pagination from '@/mixins/dataTablePagination'
import filters from '@/mixins/dataTableFilters'
import tooltipDelay from '@/mixins/tooltip'
import dateFormatter from '@/mixins/dateFormatter'
import customFilters from '@/mixins/customFilters'

import { errorHandler } from '@/utils/errorHandler'

export default {
    name: 'ProductionSitesList',
    components: {
        PageTitle,
        ConfirmDialog,
        ProductionSiteDialog
    },
    mixins: [
        pagination,
        tooltipDelay,
        dateFormatter,
        customFilters,
        filters
    ],
    beforeRouteEnter(to, from, next) {
        Promise.all([
            store.dispatch('configurations/getProductionSitesTypes'),
            store.dispatch('configurations/getPrinters'),
            store.dispatch('configurations/getAllowedReportItemTypes'),
            store.dispatch('configurations/getMachines')
        ])
            .then(responses => {
                next(vm => {
                    vm.types = responses[0].data.results
                    vm.printers = responses[1].data.results
                    vm.allowedReportItemTypes = responses[2].data.results
                    vm.machines = responses[3].data.results

                })
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    beforeRouteLeave(to, from, next) {
        this.$store.dispatch('configurations/clearProductionSitesFilters')
        next()
    },
    data() {
        return {
            productionSiteDialog: false,
            deleteProductionSiteDialog: false,
            currentProductionSite: {},
            types: [],
            printers: [],
            machines: [],
            allowedReportItemTypes: [],
            headers: [
                {
                    text: 'Nazwa',
                    value: 'name',
                    sortable: false,
                    width: 200
                },
                {
                    text: 'Numer',
                    value: 'number',
                    sortable: false
                },
                {
                    text: 'Maszyna',
                    value: 'machine',
                    sortable: false
                },
                {
                    text: 'Typ stanowiska',
                    value: 'type',
                    sortable: false
                },
                {
                    text: 'Czy można nadawać?',
                    value: 'allowsSendingOrders',
                    sortable: false,
                    align: 'center'
                },
                {
                    text: 'Akcje',
                    value: 'actions',
                    sortable: false,
                    align: 'center',
                    width: 100
                }
            ]
        }
    },
    computed: {
        productionSites() {
            return this.$store.state.configurations.productionSites.productionSites
        },
        productionSitesLoading() {
            return this.$store.state.configurations.productionSites.productionSitesLoading
        },
        savedFilters() {
            return this.$store.state.configurations.productionSites.filters
        },
        filterQuery() {
            const filters = this.filters
            let query = ''

            for (let item in filters) {
                if (filters[item] != null && filters[item] != '') {
                    query = query.concat('&', `${item}=${filters[item]}`)
                }
            }

            return query
        }
    },
    mounted() {
        this.getProductionSites()
    },
    methods: {
        getProductionSites() {
            this.$store.dispatch('configurations/getProductionSites', {
                paginate: this.paginate(),
                query: this.filterQuery
            })
                .then(response => {
                    this.handlePaginationResponse(response)
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        showCreateProductionSiteDialog() {
            this.productionSiteDialog = true
        },
        showEditProductionSiteDialog(productionSite) {
            this.currentProductionSite = productionSite
            this.productionSiteDialog = true
        },
        closeProductionSiteDialog() {
            this.productionSiteDialog = false

            setTimeout(() => {
                this.currentProductionSite = {}
            }, 500)
        },
        saveProductionSite() {
            this.productionSiteDialog = false
            this.getProductionSites()
            this.closeProductionSiteDialog()
        },
        showDeleteProductionSiteDialog(productionSite) {
            this.deleteProductionSiteDialog = true
            this.currentProductionSite = productionSite
        },
        closeDeleteProductionSiteDialog() {
            this.deleteProductionSiteDialog = false
            this.currentProductionSite = {}
        },
        deleteProductionSite(currentProductionSite) {
            this.productionSiteLoadingOnDelete = true
            this.$store.dispatch('configurations/deleteProductionSite', currentProductionSite.id)
                .then(() => {
                    this.getProductionSites()
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.closeDeleteProductionSiteDialog()
                    this.productionSiteLoadingOnDelete = false
                })
        },
        changeFilter() {
            this.$store.dispatch('configurations/setProductionSitesFilters', this.filters)
            this.resetPagination()
            this.getProductionSites()
        },
        updatePagination(pagination) {
            this.pagination = pagination
            this.evaluateSavedPagination(this.$options.name)
            if (pagination.pageCount !== 0 && !this.paginationSettings.paginationPending) {
                this.getProductionSites()
            }
            this.clearPaginationPending()
        }
    }
}
</script>
